import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';

/**
 * Light, dark, or whatever the machine says.
 *
 * Three values rather than a boolean, because "follow the system" is a real
 * answer and not the absence of one: somebody whose laptop switches at sunset
 * expects the console to switch with it, and a boolean toggle silently opts them
 * out of that the first time they touch it.
 */
export type Theme = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'dru.theme';

interface ThemeContextValue {
  /** What the user chose, including 'system'. */
  theme: Theme;
  /** What is actually on screen right now — never 'system'. */
  resolved: 'light' | 'dark';
  setTheme: (t: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStored(): Theme {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  } catch {
    // Private browsing, or storage blocked by policy. Following the system is
    // the right fallback: it is what the user has already told their machine.
  }
  return 'system';
}

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-color-scheme: dark)').matches === true;
}

/**
 * Writes the choice to the document element.
 *
 * `system` REMOVES the attribute rather than setting it to a resolved value.
 * That is what lets the CSS media query take over, so the page follows the
 * machine live — pinning `data-theme="light"` at load would freeze whatever the
 * system happened to be at that moment and never change again.
 */
function apply(theme: Theme) {
  const root = document.documentElement;
  if (theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme);
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(readStored);
  // Tracked so a component can render the right icon under 'system', and so a
  // system change while sitting on 'system' actually re-renders.
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  useEffect(() => { apply(theme); }, [theme]);

  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return;
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // The theme still applies for this page; it just will not be remembered.
      // Not worth an error dialog over.
    }
  }, []);

  const value = useMemo<ThemeContextValue>(() => ({
    theme,
    resolved: theme === 'system' ? (systemDark ? 'dark' : 'light') : theme,
    setTheme,
  }), [theme, systemDark, setTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/**
 * The theme, for a component that needs to show or change it.
 *
 * Throws without a provider rather than silently reporting light: a toggle that
 * renders and does nothing is worse than one that fails loudly in development.
 */
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme needs a <ThemeProvider> above it.');
  return ctx;
}

/**
 * The script that must run BEFORE first paint, inlined into each app's HTML.
 *
 * Without it a dark-mode user gets a white flash on every page load: React has
 * not mounted yet, so the attribute is not on `<html>` when the browser makes
 * its first paint. This is deliberately dependency-free, synchronous, and
 * tolerant of storage being unavailable.
 *
 * Kept here, next to the code that reads the same key, so the two cannot
 * disagree about the storage key or the accepted values.
 */
export const THEME_BOOT_SCRIPT = `(function(){try{
var t=localStorage.getItem('${STORAGE_KEY}');
if(t==='dark'||t==='light'){document.documentElement.setAttribute('data-theme',t);}
}catch(e){}})();`;
